import { makeStyles, Button, Popover, PopoverTrigger, PopoverSurface, Switch, RadioGroup, Radio } from '@fluentui/react-components';
import { Settings20Regular } from '@fluentui/react-icons';
import { Logo } from '@brand/Logo';
import { getBrandConfig } from '../brands/brand-registry';
import { getVoiceMetadata } from '../api/api';
import { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageProvider';

const useStyles = makeStyles({
  root: {
    position: 'sticky', top: 0, zIndex: 10,
    flexShrink: 0,
    height: 'var(--header-height, 60px)',
    background: 'var(--header-bg)', // brand-specific header background
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    '@media (max-width: 600px)': {
      height: 'var(--header-height-mobile, 50px)',
      gridTemplateColumns: 'auto 1fr auto',
    },
  },
  wedge: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '30%',
    height: '100%',
    background: 'var(--header-brand-bg)', // Blue wedge background
    clipPath: 'polygon(0 0, 85% 0, 70% 100%, 0% 100%)',
    zIndex: 1,
    pointerEvents: 'none',
    '@media (min-width: 600px)': {
      minWidth: '350px',
    },
    '@media (max-width: 600px)': {
      width: '70%',
      minWidth: '200px',
    }
  },
  left: { 
    position: 'relative', 
    display: 'flex', 
    alignItems: 'center',
    zIndex: 2, // Logo appears on top of blue wedge
    '& img': {
      display: 'block',
      height: 'auto',
      maxWidth: '200px',
    },
    '@media (max-width: 600px)': {
      '& img': {
        marginLeft: '0px',
        maxWidth: '150px',
      }
    }
  },
  center: {
    textAlign: 'center',
    fontWeight: 'var(--header-title-weight, 700)',
    color: 'var(--header-text)',
    fontSize: 'var(--header-title-size, 20px)',
    letterSpacing: 'var(--header-title-spacing, 0)',
    lineHeight: 'var(--header-title-line-height, 1.2)',
    fontFamily: 'var(--header-title-font, "Lato", "Segoe UI", "Helvetica Neue", Arial, sans-serif)',
    userSelect: 'none',
    zIndex: 2,
    '@media (max-width: 600px)': {
      fontSize: 'var(--header-title-size-mobile, 28px)',
      display: 'flex',
      justifyContent: 'flex-end',
    }
  },
  right: { 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginRight: '8px',
    gap: '8px',
    zIndex: 2, // Settings appear on top of blue wedge
    '@media (max-width: 600px)': {
      paddingLeft: '8px',
    }
  },
  languageSwitcher: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid rgba(255, 255, 255, 0.45)',
    borderRadius: '9999px',
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  languageButton: {
    minWidth: '40px',
    minHeight: '30px',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--header-text)',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    cursor: 'pointer',
    padding: '4px 10px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.16)',
    },
    '&:focus-visible': {
      outline: '2px solid #FFFFFF',
      outlineOffset: '-2px',
      boxShadow: 'inset 0 0 0 4px #242424',
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '28px',
      fontSize: '11px',
      padding: '4px 8px',
    },
  },
  languageButtonActive: {
    backgroundColor: 'var(--header-brand-bg)',
    color: 'var(--header-text, #FFFFFF)',
    '&:hover': {
      backgroundColor: 'var(--header-brand-bg)',
    },
  },
  settingsButton: {
    color: 'var(--header-text) !important',
    '&:hover': {
      backgroundColor: 'var(--header-nav-inactive, rgba(163, 145, 97, 0.2)) !important',
    }
  },
  settingsPopover: {
    padding: 'var(--spacing-md, 16px)',
    minWidth: 'var(--settings-popover-min-width, 280px)',
    maxWidth: 'var(--settings-popover-max-width, 350px)',
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: 'var(--radius-card)',
    boxShadow: 'var(--shadow-popover, 0 4px 12px rgba(0, 0, 0, 0.15))'
  },
  settingsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md, 16px)'
  },
  settingsSection: {
    paddingBottom: 'var(--spacing-sm, 12px)',
    borderBottom: '1px solid var(--border-secondary)'
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs, 8px)'
  },
  settingsTitle: {
    fontSize: 'var(--text-sm-size, 14px)',
    fontWeight: 'var(--text-semibold-weight, 600)',
    color: 'var(--text-primary)',
    marginBottom: 'var(--spacing-xs, 8px)'
  },
  speedOptions: {
    display: 'flex',
    gap: 'var(--spacing-sm, 12px)',
    flexWrap: 'wrap'
  },
  settingsSelect: {
    width: '100%',
    padding: 'var(--spacing-xs, 8px) var(--spacing-sm, 12px)',
    border: '1px solid var(--border-primary)',
    borderRadius: 'var(--radius-field)',
    fontSize: 'var(--text-sm-size, 14px)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)'
  },
  settingsSelection: {
    marginTop: 'var(--spacing-xs, 8px)',
    padding: 'var(--spacing-xs, 6px) var(--spacing-sm, 10px)',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs-size, 11px)',
    color: 'var(--text-tertiary)'
  }
});

export default function Masthead(){
  const s = useStyles();
  const { language, setLanguage } = useLanguage();
  
  // Get brand configuration for dynamic app name
  const brandConfig = getBrandConfig();
  const speechProvider = (brandConfig.features as { speechProvider?: 'openai' | 'abair' }).speechProvider || 'openai';
  const showVoiceSettings = 'showVoiceSettings' in brandConfig.features
    ? brandConfig.features.showVoiceSettings
    : true;
  const configuredVoice = 'synthesisVoice' in brandConfig.features
    ? brandConfig.features.synthesisVoice
    : undefined;
  const defaultVoice = configuredVoice || (speechProvider === 'abair' ? 'ga_CO_snc_piper' : 'en-IE-EmilyNeural');
  
  // Settings state
  const [selectedVoice, setSelectedVoice] = useState(defaultVoice);
  const [availableVoices, setAvailableVoices] = useState<any[]>([]);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [briefResponse, setBriefResponse] = useState(false);
  const [prefetchSpeech, setPrefetchSpeech] = useState(false);
  const [speechSpeed, setSpeechSpeed] = useState<'normal' | 'faster'>('normal');

  const loadVoices = async () => {
    if (voicesLoaded) return;
    
    try {
      const response = await getVoiceMetadata(speechProvider);
      const voices = response.voices || [];
      setAvailableVoices(voices);
      setVoicesLoaded(true);

      // Validate stored voice against the returned list.
      // If it's no longer valid (e.g. switching from OpenAI 'alloy' to Azure Speech),
      // reset to the first available voice.
      if (voices.length > 0 && speechProvider !== 'abair') {
        const stored = localStorage.getItem('selectedVoice');
        const isValid = stored && voices.some((v: any) => v.name === stored);
        if (!isValid) {
          const first = voices[0].name;
          setSelectedVoice(first);
          localStorage.setItem('selectedVoice', first);
        }
      }
    } catch (error) {
      console.error("Failed to load voices:", error);
    }
  };

  const handleVoiceChange = (_event: any, data: any) => {
    setSelectedVoice(data.value);
    localStorage.setItem('selectedVoice', data.value);
  };

  const handleToggleBriefResponse = () => {
    if (brandConfig.features.enableBriefResponse) {
      const newValue = !briefResponse;
      setBriefResponse(newValue);
      localStorage.setItem('briefResponse', newValue.toString());
    }
  };

  const handleTogglePrefetchSpeech = () => {
    const newValue = !prefetchSpeech;
    setPrefetchSpeech(newValue);
    localStorage.setItem('prefetchSpeech', newValue.toString());
  };

  const handleSpeechSpeedChange = (value: 'normal' | 'faster') => {
    setSpeechSpeed(value);
    localStorage.setItem('speechSpeed', value);
  };

  // Load saved preferences on mount
  useEffect(() => {
    const savedVoice = localStorage.getItem('selectedVoice');
    if (savedVoice) {
      setSelectedVoice(savedVoice);
    } else {
      setSelectedVoice(defaultVoice);
    }
    
    const savedBrief = localStorage.getItem('briefResponse');
    if (savedBrief) {
      setBriefResponse(savedBrief === 'true');
    }

    const savedPrefetch = localStorage.getItem('prefetchSpeech');
    if (savedPrefetch) {
      setPrefetchSpeech(savedPrefetch === 'true');
    }

    const savedSpeed = localStorage.getItem('speechSpeed');
    if (savedSpeed === 'normal' || savedSpeed === 'faster') {
      setSpeechSpeed(savedSpeed);
    }
  }, [defaultVoice]);
  
  return (
    <header className={s.root}>
      <div className={s.wedge}/>
      <div className={s.left}>
        <a href={brandConfig.websiteLink} aria-label={brandConfig.accessibility?.homeLabel ?? 'Home'}>
          <Logo 
            variant="white"
            language={language}
          />
        </a>
      </div>
      <div className={s.center}>
        {/* Render formatted app name if config provides parts, otherwise plain text */}
        {brandConfig.titleFormatting?.appNameParts ? (
          brandConfig.titleFormatting.appNameParts.map((part: { text: string; bold: boolean }, index: number) => 
            part.bold ? <strong key={index}>{part.text}</strong> : <span key={index}>{part.text}</span>
          )
        ) : (
          brandConfig.appName
        )}
      </div>
      <div className={s.right}>
        {brandConfig.features.showLanguageSwitcher && (
          <div
            className={s.languageSwitcher}
            role="group"
            aria-label={brandConfig.header?.languageSwitcher?.groupLabel ?? 'Language switcher'}
          >
            <button
              type="button"
              className={`${s.languageButton} ${language === 'en' ? s.languageButtonActive : ''}`}
              onClick={() => setLanguage('en')}
              aria-pressed={language === 'en'}
              aria-label={brandConfig.header?.languageSwitcher?.englishAriaLabel ?? 'Switch language to English'}
              title={brandConfig.header?.languageSwitcher?.englishLabel ?? 'English'}
            >
              {brandConfig.header?.languageSwitcher?.englishShortLabel ?? 'EN'}
            </button>
            <button
              type="button"
              className={`${s.languageButton} ${language === 'ga' ? s.languageButtonActive : ''}`}
              onClick={() => setLanguage('ga')}
              aria-pressed={language === 'ga'}
              aria-label={brandConfig.header?.languageSwitcher?.irishAriaLabel ?? 'Switch language to Irish'}
              title={brandConfig.header?.languageSwitcher?.irishLabel ?? 'Irish'}
            >
              {brandConfig.header?.languageSwitcher?.irishShortLabel ?? 'GA'}
            </button>
          </div>
        )}

        {/* Voice Selection Settings */}
        {brandConfig.features.enableVoiceSynthesis && showVoiceSettings && (
          <Popover 
            withArrow
            positioning="below-end"
            size="medium"
          >
            <PopoverTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                title={brandConfig.header.voiceSettings.buttonTitle}
                size="small"
                icon={<Settings20Regular />}
                className={s.settingsButton}
                onClick={loadVoices}
              />
            </PopoverTrigger>
            <PopoverSurface 
              tabIndex={-1} 
              className={s.settingsPopover}
            >
              <div className={s.settingsContent}>
                <div className={s.settingsSection}>
                  <div className={s.toggleGroup}>
                    {brandConfig.features.enableBriefResponse && (
                      <Switch
                        checked={briefResponse}
                        onChange={handleToggleBriefResponse}
                        label={brandConfig.header.voiceSettings.briefLabel}
                      />
                    )}
                    <Switch
                      checked={prefetchSpeech}
                      onChange={handleTogglePrefetchSpeech}
                      label={brandConfig.header.voiceSettings?.prefetchLabel ?? 'Prefetch speech audio'}
                    />
                  </div>
                </div>

                <div className={s.settingsSection}>
                  <div className={s.settingsTitle}>
                    {brandConfig.header.voiceSettings?.speedLabel ?? 'Playback speed'}
                  </div>
                  <RadioGroup
                    layout="horizontal"
                    value={speechSpeed}
                    onChange={(_, data) => handleSpeechSpeedChange((data.value as 'normal' | 'faster') || 'normal')}
                    className={s.speedOptions}
                  >
                    <Radio
                      value="normal"
                      label={brandConfig.header.voiceSettings?.speedOptions?.normal ?? 'Normal'}
                    />
                    <Radio
                      value="faster"
                      label={brandConfig.header.voiceSettings?.speedOptions?.faster ?? 'Faster'}
                    />
                  </RadioGroup>
                </div>
                
                {/* Voice Selection Section */}
                <div>
                  <div className={s.settingsTitle}>
                    {brandConfig.header.voiceSettings.voiceSelectionLabel}
                  </div>
                
                <select
                  value={selectedVoice}
                  onChange={(e) => handleVoiceChange(null, { value: e.target.value })}
                  className={s.settingsSelect}
                >
                  {availableVoices.map((voice) => {
                    if (voice?.name && voice?.displayName) {
                      return (
                        <option key={voice.name} value={voice.name}>
                          {voice.displayName}
                        </option>
                      );
                    }

                    const piperVoiceName = voice?.voicenames?.find((name: string) => name.includes('piper'));
                    if (!piperVoiceName) return null;

                    const displayText = `${voice.name} (${voice.locale})`;

                    return (
                      <option key={piperVoiceName} value={piperVoiceName}>
                        {displayText}
                      </option>
                    );
                  })}
                </select>
                
                {/* Current Selection Display */}
                {selectedVoice && (
                  <div className={s.settingsSelection}>
                    {brandConfig.header.voiceSettings.selectedPrefix}{(() => {
                      const selectedVoiceData = availableVoices.find(v =>
                        v?.name === selectedVoice ||
                        v?.voicenames?.find((name: string) => name.includes('piper')) === selectedVoice
                      );
                      if (selectedVoiceData?.displayName) {
                        return selectedVoiceData.displayName;
                      }
                      if (selectedVoiceData) {
                        return `${selectedVoiceData.name} (${selectedVoiceData.locale})`;
                      }
                      return brandConfig.header.voiceSettings.defaultVoice;
                    })()}
                  </div>
                )}
                </div>
              </div>
            </PopoverSurface>
          </Popover>
        )}
      </div>
    </header>
  );
}
